// AgentesGrupos.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import AgentScoreboardModal from "./AgentScoreboardModal";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AgentesGrupos = () => {
  const db = getFirestore();

  const [selectedTab, setSelectedTab] = useState("agentes");
  const [agents, setAgents] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [migratingRutas, setMigratingRutas] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  // Estados de carga global
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "Cobrador"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const agentsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgents(agentsData);
      setLoadingAgents(false);
    });
    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    const q = query(collection(db, "negocios"), where("status", "==", "activo"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const businessesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBusinesses(businessesData);
      setLoadingBusinesses(false);
    });
    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "cobros"), (snapshot) => {
      const paymentsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        let parsedDate;
        if (typeof data.date === "string") {
          parsedDate = dayjs(data.date, "D [de] MMMM [de] YYYY, h:mm:ss a [UTC]Z").toDate();
        } else if (data.date && data.date.toDate) {
          parsedDate = data.date.toDate();
        } else {
          parsedDate = new Date();
        }
        return {
          id: doc.id,
          ...data,
          paymentDate: parsedDate,
          amount: Number(data.netAmount !== undefined ? data.netAmount : data.amount || 0),
          businessId: data.businessId,
          receiptUrl: data.receiptUrl,
        };
      });
      setPayments(paymentsData);
      setLoadingPayments(false);
    });
    return () => unsubscribe();
  }, [db]);

  const businessCountByAgent = useMemo(() => {
    const mapping = {};
    businesses.forEach((biz) => {
      if (biz.agentId) {
        mapping[biz.agentId] = (mapping[biz.agentId] || 0) + 1;
      }
    });
    return mapping;
  }, [businesses]);

  const getActiveBusinessCount = useCallback(
    (agentId) => businessCountByAgent[agentId] || 0,
    [businessCountByAgent]
  );

  const handleDownloadPadron = useCallback(
    (agent) => {
      if (!businesses || businesses.length === 0) {
        alert("No se encontraron negocios.");
        return;
      }
      const assignedBusinesses = businesses.filter((biz) => biz.agentId === agent.id);
      if (assignedBusinesses.length === 0) {
        alert("No hay negocios asignados a este agente.");
        return;
      }
      const data = assignedBusinesses.map((biz) => ({
        ID: biz.id,
        "Nombre del Negocio": biz.name || "Sin nombre",
        Dirección: biz.address || "Sin dirección",
        Estado: biz.status,
        "Teléfono del Negocio": biz.phone || "Sin teléfono",
        Quota: biz.quota !== undefined && biz.quota !== null ? biz.quota : "Sin quota",
        "Agente": agent.name,
      }));
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Negocios");
      const currentDate = dayjs().format("DD-MM-YY");
      const fileName = `padron_${agent.name}_${currentDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    },
    [businesses]
  );

  // Handler para cambiar rutas de un agente
  const handleToggleRuta = async (agentId, ruta, currentRutas) => {
    const rutas = currentRutas || [];
    const newRutas = rutas.includes(ruta)
      ? rutas.filter((r) => r !== ruta)
      : [...rutas, ruta].sort();
    try {
      await updateDoc(doc(db, "users", agentId), { rutas: newRutas });
    } catch (error) {
      console.error("Error actualizando rutas:", error);
    }
  };

  // Asignar rutas masivamente a negocios según el agente que los registró
  const handleMigrarRutas = async () => {
    if (!window.confirm("Esto asignará la ruta a TODOS los negocios sin ruta, según el agente que los registró. ¿Continuar?")) return;
    setMigratingRutas(true);
    setMigrationResult(null);
    try {
      // Mapa de nombres conocidos a rutas por defecto
      const DEFAULT_ROUTES = {
        "alma": "1", "pedro": "2", "eliud": "3", "catalino": "4",
      };

      // Crear mapa agentId -> ruta
      const agentRutaMap = {};
      for (const agent of agents) {
        // Si ya tiene rutas asignadas, usar la primera
        if (agent.rutas && agent.rutas.length > 0) {
          agentRutaMap[agent.id] = agent.rutas[0];
        } else {
          // Si no tiene rutas, intentar asignar por nombre conocido
          const firstName = (agent.name || "").toLowerCase().split(" ")[0];
          const defaultRuta = DEFAULT_ROUTES[firstName];
          if (defaultRuta) {
            // Asignar la ruta al agente también
            await updateDoc(doc(db, "users", agent.id), { rutas: [defaultRuta] });
            agentRutaMap[agent.id] = defaultRuta;
          }
        }
      }

      // Obtener todos los negocios
      const negociosSnap = await getDocs(collection(db, "negocios"));
      let actualizados = 0;
      let sinAgente = 0;
      let yaConRuta = 0;

      const batch = writeBatch(db);
      let batchCount = 0;

      for (const docSnap of negociosSnap.docs) {
        const data = docSnap.data();
        if (data.ruta) { yaConRuta++; continue; }

        const ruta = agentRutaMap[data.agentId];
        if (ruta) {
          batch.update(docSnap.ref, { ruta });
          actualizados++;
          batchCount++;
          if (batchCount >= 490) {
            await batch.commit();
            batchCount = 0;
          }
        } else {
          sinAgente++;
        }
      }

      if (batchCount > 0) await batch.commit();

      setMigrationResult({
        type: "success",
        message: `Rutas asignadas: ${actualizados} negocios. Ya tenían ruta: ${yaConRuta}. Sin agente con ruta: ${sinAgente}.`,
      });
    } catch (error) {
      console.error("Error migrando rutas:", error);
      setMigrationResult({ type: "error", message: "Error al asignar rutas: " + error.message });
    } finally {
      setMigratingRutas(false);
    }
  };

  const isLoading = loadingAgents || loadingBusinesses || loadingPayments;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <svg
          className="animate-spin h-12 w-12 text-blue-500 mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          ></path>
        </svg>
        <p className="text-lg">Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Agentes y Grupos</h1>
      <div className="mb-4 border-b">
        <nav className="flex space-x-4">
          <button
            onClick={() => setSelectedTab("agentes")}
            className={`px-4 py-2 focus:outline-none ${
              selectedTab === "agentes" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
          >
            Agentes
          </button>
          <button
            onClick={() => setSelectedTab("grupos")}
            className={`px-4 py-2 focus:outline-none ${
              selectedTab === "grupos" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
          >
            Grupos
          </button>
        </nav>
      </div>

      {selectedTab === "agentes" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Lista de Agentes</h2>
            <button
              onClick={handleMigrarRutas}
              disabled={migratingRutas}
              className="px-4 py-2 bg-orange-500 text-white text-sm rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {migratingRutas ? "Asignando..." : "Asignar Rutas a Negocios"}
            </button>
          </div>
          {migrationResult && (
            <div className={`mb-4 p-3 rounded text-sm ${migrationResult.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {migrationResult.message}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded-lg">
              <thead>
                <tr className="bg-gray-200 text-gray-700 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Nombre</th>
                  <th className="py-3 px-6 text-center">Rutas Asignadas</th>
                  <th className="py-3 px-6 text-left">Negocios Activos</th>
                  <th className="py-3 px-6 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm font-light">
                {agents.length > 0 ? (
                  agents.map((agent) => (
                    <tr key={agent.id} className="border-b border-gray-200 hover:bg-gray-100">
                      <td className="py-3 px-6 text-left whitespace-nowrap font-medium">{agent.name}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {["1", "2", "3", "4", "5"].map((ruta) => (
                            <label key={ruta} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(agent.rutas || []).includes(ruta)}
                                onChange={() => handleToggleRuta(agent.id, ruta, agent.rutas)}
                                className="accent-blue-500"
                              />
                              <span className="text-xs">{ruta}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-6 text-left">{getActiveBusinessCount(agent.id)}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => setSelectedAgent(agent)}
                            className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                          >
                            Ver Score
                          </button>
                          <button
                            onClick={() => handleDownloadPadron(agent)}
                            className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                          >
                            Descargar Padrón
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-3 text-gray-500 italic">
                      No se encontraron agentes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === "grupos" && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Grupos</h2>
          <p>Aún no hay grupos creados.</p>
        </div>
      )}

      {selectedAgent && (
        <AgentScoreboardModal
          agent={selectedAgent}
          businesses={businesses}
          payments={payments}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
};

export default AgentesGrupos;
